import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Lock, Phone, Mail, MapPin, Car, LogOut, RefreshCw, ClipboardList, Check } from "lucide-react";
import { SEO } from "../components/SEO";
import { api } from "../lib/api";

const BRAKE_LABELS = {
  front: "Front brakes",
  rear: "Rear brakes",
  both: "Front & rear",
  inspection: "Not sure",
};

const SERVICE_LABELS = {
  brakes: "Brake Service",
  "wheel-bearing": "Wheel Bearing / Hub",
  suspension: "Suspension",
  alternator: "Alternator",
  starter: "Starter",
  "tune-up": "Tune-Up / Maintenance",
  other: "Other",
};

const fmtDate = (iso) =>
  new Date(iso).toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" });

export default function AdminQuotes() {
  const [authed, setAuthed] = useState(null);
  const [passcode, setPasscode] = useState("");
  const [busy, setBusy] = useState(false);
  const [quotes, setQuotes] = useState([]);

  const load = () =>
    api
      .adminQuotes()
      .then((r) => setQuotes(r.quotes))
      .catch(() => setAuthed(false));

  useEffect(() => {
    api
      .adminCheck()
      .then(() => {
        setAuthed(true);
        load();
      })
      .catch(() => setAuthed(false));
  }, []);

  const login = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.adminLogin(passcode);
      setAuthed(true);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  const logout = async () => {
    await api.adminLogout().catch(() => {});
    setAuthed(false);
    setQuotes([]);
    setPasscode("");
  };

  const toggleDone = async (q) => {
    const next = q.status === "done" ? "new" : "done";
    setQuotes((qs) => qs.map((x) => (x.id === q.id ? { ...x, status: next } : x)));
    try {
      await api.adminSetQuoteStatus(q.id, next);
    } catch {
      toast.error("Couldn't update — try again");
      load();
    }
  };

  if (authed === null) {
    return <div className="mx-auto max-w-md px-4 py-24 text-center text-slate-400">Loading…</div>;
  }

  if (!authed) {
    return (
      <>
        <SEO title="Owner Login | My Mobile Mechanic" description="Private owner area." path="/admin" />
        <section className="mx-auto max-w-md px-4 py-20 sm:py-28">
          <form
            data-testid="admin-login-form"
            onSubmit={login}
            className="border border-edge bg-slate2 p-8 text-center"
          >
            <span className="mx-auto flex h-14 w-14 items-center justify-center bg-crimson">
              <Lock className="h-7 w-7 text-white" />
            </span>
            <h1 className="mt-5 font-display text-3xl font-black uppercase tracking-tight text-slate-50">
              Owner Access
            </h1>
            <p className="mt-2 text-sm text-slate-400">Enter your passcode to view quote requests.</p>
            <input
              data-testid="admin-passcode-input"
              type="password"
              required
              value={passcode}
              onChange={(e) => setPasscode(e.target.value)}
              placeholder="Passcode"
              className="mt-6 min-h-[52px] w-full border border-edge bg-white px-4 text-center text-base text-slate-100 placeholder:text-slate-400 focus:border-crimson focus:outline-none"
            />
            <button
              type="submit"
              data-testid="admin-login-button"
              disabled={busy}
              className="mt-4 min-h-[52px] w-full bg-crimson font-display text-xl font-bold uppercase text-white transition-colors hover:bg-crimsondark disabled:opacity-60"
            >
              {busy ? "Checking…" : "Unlock"}
            </button>
          </form>
        </section>
      </>
    );
  }

  return (
    <>
      <SEO title="Quote Requests | My Mobile Mechanic" description="Private owner area." path="/admin" />
      <section data-testid="admin-quotes-page" className="mx-auto max-w-5xl px-4 py-14 sm:px-6">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-crimson">Private · Owner only</p>
            <h1 className="mt-2 font-display text-4xl font-black uppercase tracking-tight text-slate-50">
              Quote Requests <span className="text-crimson">({quotes.length})</span>
            </h1>
          </div>
          <div className="flex gap-2">
            <button
              data-testid="admin-refresh-button"
              onClick={load}
              className="flex min-h-[48px] items-center gap-2 border-2 border-edgehi px-5 font-display text-base font-bold uppercase text-slate-100 transition-colors hover:border-crimson hover:text-crimson"
            >
              <RefreshCw className="h-4 w-4" /> Refresh
            </button>
            <button
              data-testid="admin-logout-button"
              onClick={logout}
              className="flex min-h-[48px] items-center gap-2 border-2 border-edgehi px-5 font-display text-base font-bold uppercase text-slate-100 transition-colors hover:border-amber2 hover:text-amber2"
            >
              <LogOut className="h-4 w-4" /> Log Out
            </button>
          </div>
        </div>

        {quotes.length === 0 ? (
          <div className="mt-10 border border-edge bg-slate2 p-10 text-center">
            <ClipboardList className="mx-auto h-10 w-10 text-slate-400" />
            <p className="mt-4 text-slate-400">No quote requests yet. New ones appear here — and in your email — the moment they come in.</p>
          </div>
        ) : (
          <div data-testid="admin-quote-list" className="mt-8 space-y-4">
            {quotes.map((q, i) => (
              <article
                key={q.id || i}
                data-testid={`admin-quote-${i}`}
                className={`border p-5 transition-all sm:p-6 ${
                  q.status === "done" ? "border-emerald-300 bg-emerald-50/60 opacity-70" : "border-edge bg-slate2"
                }`}
              >
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <p className="font-display text-2xl font-bold uppercase tracking-tight text-slate-50">{q.name}</p>
                    <p className="font-mono text-[11px] uppercase tracking-widest text-slate-500">{fmtDate(q.created_at)}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    {q.status === "done" && (
                      <span className="bg-emerald-500 px-3 py-1.5 font-mono text-[11px] font-bold uppercase tracking-widest text-white">
                        Called back
                      </span>
                    )}
                    <span className="bg-crimson px-3 py-1.5 font-mono text-[11px] font-bold uppercase tracking-widest text-white">
                      {SERVICE_LABELS[q.service] || "Brake Service"}{q.brakes ? ` · ${BRAKE_LABELS[q.brakes] || q.brakes}` : ""}
                    </span>
                    <button
                      data-testid={`admin-quote-${i}-done-toggle`}
                      onClick={() => toggleDone(q)}
                      title={q.status === "done" ? "Mark as not done" : "Mark as called back"}
                      aria-pressed={q.status === "done"}
                      className={`flex h-9 w-9 items-center justify-center rounded-full border-2 transition-colors ${
                        q.status === "done"
                          ? "border-emerald-500 bg-emerald-500 text-white"
                          : "border-edgehi text-slate-400 hover:border-emerald-500 hover:text-emerald-500"
                      }`}
                    >
                      <Check className="h-5 w-5" />
                    </button>
                  </div>
                </div>
                <div className="mt-4 grid gap-3 text-sm sm:grid-cols-2">
                  <a href={`tel:${q.phone}`} data-testid={`admin-quote-${i}-call`} className="flex items-center gap-2 font-bold text-crimson hover:text-crimsondark">
                    <Phone className="h-4 w-4" /> {q.phone}
                  </a>
                  <a href={`mailto:${q.email}`} data-testid={`admin-quote-${i}-email`} className="flex items-center gap-2 text-slate-200 hover:text-crimson">
                    <Mail className="h-4 w-4 text-slate-400" /> {q.email}
                  </a>
                  <p className="flex items-center gap-2 text-slate-200">
                    <Car className="h-4 w-4 text-slate-400" /> {q.year} {q.make} {q.model}{q.engine ? ` · ${q.engine}` : ""}
                  </p>
                  <p className="flex items-center gap-2 text-slate-200">
                    <MapPin className="h-4 w-4 text-slate-400" /> ZIP {q.zip_code}
                  </p>
                </div>
                {q.description && (
                  <p className="mt-3 border-t border-edge pt-3 text-sm leading-relaxed text-slate-300">
                    <span className="font-mono text-[10px] uppercase tracking-widest text-slate-500">Problem: </span>
                    {q.description}
                  </p>
                )}
              </article>
            ))}
          </div>
        )}
      </section>
    </>
  );
}
